/**
 * SubscribePage — autonomous subscription flow.
 *
 * 3 steps in one page (no jumping between routes):
 *   1) Plan selector (Free / Starter / Pro / Agency) + billing period toggle
 *   2) Account data (registration form, including account type / NIF / company)
 *   3) Confirmation / checkout (Stripe placeholder — falls back to "pending payment")
 *
 * Designed to be autonomous from /login?tab=register so we can keep the simpler
 * normal registration flow untouched.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CreditCard,
  Loader2,
  Lock,
  Sparkles,
  Building2,
  User as UserIcon,
  Briefcase,
  Eye,
  EyeOff,
  Gift,
} from "lucide-react";
import { toast } from "../../components/ui/sonner";
import useAuthStore from "../../store/useAuthStore";
import Logo from "../../components/common/Logo";

const COUNTRY_CODES = [
  { code: "+351", flag: "🇵🇹", name: "Portugal" },
  { code: "+34",  flag: "🇪🇸", name: "Espanha" },
  { code: "+33",  flag: "🇫🇷", name: "França" },
  { code: "+44",  flag: "🇬🇧", name: "Reino Unido" },
  { code: "+49",  flag: "🇩🇪", name: "Alemanha" },
  { code: "+39",  flag: "🇮🇹", name: "Itália" },
  { code: "+1",   flag: "🇺🇸", name: "EUA / Canadá" },
  { code: "+55",  flag: "🇧🇷", name: "Brasil" },
  { code: "+244", flag: "🇦🇴", name: "Angola" },
  { code: "+258", flag: "🇲🇿", name: "Moçambique" },
  { code: "+238", flag: "🇨🇻", name: "Cabo Verde" },
  { code: "+41",  flag: "🇨🇭", name: "Suíça" },
  { code: "+352", flag: "🇱🇺", name: "Luxemburgo" },
  { code: "+31",  flag: "🇳🇱", name: "Países Baixos" },
];

const PLANS = [
  {
    id: "free",
    name: "Gratuito",
    monthly: 0,
    bullets: ["1 QR Code", "Cartão virtual", "Captação de leads", "Branding IMOCODE visível"],
    accent: "border-gray-200",
  },
  {
    id: "starter",
    name: "Starter",
    monthly: 9.9,
    bullets: ["6 QR Codes", "Cartão virtual", "Leads ilimitados", "Estatísticas básicas"],
    accent: "border-emerald-200",
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 29.9,
    bullets: [
      "21 QR Codes",
      "Estatísticas avançadas",
      "Sem branding IMOCODE",
      "Exportação de dados",
      "Suporte prioritário",
    ],
    accent: "border-blue-300",
    popular: true,
  },
  {
    id: "agency",
    name: "Agência",
    monthly: null,
    bullets: ["QR personalizados", "Multi-utilizadores", "Painel agência", "White-label", "Gestor dedicado"],
    accent: "border-gray-700",
  },
];

const BILLING_OPTIONS = [
  { id: "monthly", label: "Mensal", multiplier: 1, factor: 1, save: null },
  { id: "semiannual", label: "Semestral", multiplier: 6, factor: 0.9, save: "10%" },
  { id: "annual", label: "Anual", multiplier: 12, factor: 0.83, save: "17%" },
];

const fmt = (v) =>
  typeof v === "number" ? `€${v.toFixed(2).replace(".", ",")}` : "Sob consulta";

const SubscribePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAuthStore();

  const initialPlan = searchParams.get("plan") || "pro";
  const [step, setStep] = useState(1);
  const [planId, setPlanId] = useState(
    PLANS.find((p) => p.id === initialPlan)?.id || "pro"
  );
  const [billing, setBilling] = useState("monthly");
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [accountType, setAccountType] = useState("agent");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+351");
  const [nif, setNif] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [referralCode, setReferralCode] = useState("");

  // Kit opt-in — works for ALL plans now. Subscription (service) and Tag
  // purchase (physical product) are two separate billing lines. The Tag is
  // optional: the user can buy one or more physical tags together with the
  // subscription at a per-plan discounted price.
  const [wantKit, setWantKit] = useState(false);
  const [kitQty, setKitQty] = useState(1);
  const [kitTemplate, setKitTemplate] = useState("predefinido");
  const [kitAddress, setKitAddress] = useState("");
  const [kitPostalCode, setKitPostalCode] = useState("");
  const [kitCity, setKitCity] = useState("");
  const [kitPricing, setKitPricing] = useState(null);

  // Fetch the pricing config once (admin can change it from the Admin Panel).
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/public/config/kit-pricing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setKitPricing(d))
      .catch(() => {});
  }, []);

  const plan = useMemo(() => PLANS.find((p) => p.id === planId) || PLANS[0], [planId]);
  const billingOpt = useMemo(
    () => BILLING_OPTIONS.find((b) => b.id === billing) || BILLING_OPTIONS[0],
    [billing]
  );

  const total = useMemo(() => {
    if (plan.monthly === null) return null;
    if (plan.monthly === 0) return 0;
    return +(plan.monthly * billingOpt.multiplier * billingOpt.factor).toFixed(2);
  }, [plan, billingOpt]);

  const monthlyEquivalent = useMemo(() => {
    if (plan.monthly === null || plan.monthly === 0) return null;
    return +(plan.monthly * billingOpt.factor).toFixed(2);
  }, [plan, billingOpt]);

  // --- Tag (Kit) pricing derived from admin config -----------------------
  // Falls back to safe defaults when the config hasn't loaded yet so the UI
  // doesn't flash empty values.
  const kitForPlan = useMemo(() => {
    if (!kitPricing) {
      return {
        base_price_eur: 4,
        unit_price_eur: 4,
        discount_pct: 0,
        max_qty: 1,
        enabled: true,
        savings_per_unit_eur: 0,
        shipping_eur: 5,
        free_shipping_threshold_qty: 0,
        template_options: ["predefinido", "personalizado"],
        personalization_surcharge_eur: 0,
      };
    }
    const planCfg = kitPricing.plans?.[plan.id] || {
      unit_price_eur: kitPricing.base_price_eur,
      discount_pct: 0,
      max_qty: 1,
      enabled: true,
      savings_per_unit_eur: 0,
    };
    return {
      ...planCfg,
      base_price_eur: kitPricing.base_price_eur,
      shipping_eur: kitPricing.shipping_eur,
      free_shipping_threshold_qty: kitPricing.free_shipping_threshold_qty || 0,
      template_options: kitPricing.template_options || ["predefinido", "personalizado"],
      personalization_surcharge_eur: kitPricing.personalization_surcharge_eur || 0,
    };
  }, [kitPricing, plan.id]);

  // Clamp kitQty whenever plan / max changes
  useEffect(() => {
    if (!wantKit) return;
    const maxQ = Math.max(1, kitForPlan.max_qty || 1);
    if (kitQty > maxQ) setKitQty(maxQ);
    if (kitQty < 1) setKitQty(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.id, kitForPlan.max_qty, wantKit]);

  const kitCost = useMemo(() => {
    if (!wantKit) {
      return { unit: 0, subtotal: 0, shipping: 0, total: 0, perso: 0 };
    }
    const personaSur =
      kitTemplate === "personalizado" ? kitForPlan.personalization_surcharge_eur : 0;
    const unit = +(kitForPlan.unit_price_eur + personaSur).toFixed(2);
    const subtotal = +(unit * kitQty).toFixed(2);
    let shipping = kitForPlan.shipping_eur;
    if (
      kitForPlan.free_shipping_threshold_qty > 0 &&
      kitQty >= kitForPlan.free_shipping_threshold_qty
    ) {
      shipping = 0;
    }
    shipping = +shipping.toFixed(2);
    return {
      unit,
      subtotal,
      shipping,
      total: +(subtotal + shipping).toFixed(2),
      perso: personaSur,
    };
  }, [wantKit, kitQty, kitTemplate, kitForPlan]);

  const goToStep = (n) => {
    if (n < 1 || n > 3) return;
    if (n === 2 && plan.monthly === null) {
      toast.info("O plano Agência exige consultoria. A redirecionar...");
      navigate("/?contact=sales");
      return;
    }
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (initialPlan === "agency") {
      navigate("/?contact=sales");
    }
  }, [initialPlan, navigate]);

  // Handle Stripe cancel return: show friendly notice + clean URL.
  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info(
        "Pagamento cancelado. A tua conta foi criada — podes terminar a subscrição quando quiseres.",
        { duration: 6000 }
      );
      // Strip the param so a refresh doesn't fire the toast again.
      const url = new URL(window.location.href);
      url.searchParams.delete("cancelled");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Preenche os campos obrigatórios");
      return;
    }
    if (!phone || phone.replace(/\D/g, "").length < 6) {
      toast.error("Telemóvel obrigatório (mínimo 6 dígitos)");
      return;
    }
    // Validate kit shipping address whenever the user opted in (any plan).
    if (wantKit) {
      if (!kitAddress.trim() || !kitPostalCode.trim() || !kitCity.trim()) {
        toast.error("Preenche a morada de entrega da Tag");
        return;
      }
    }
    setSubmitting(true);
    try {
      const fullPhone = `${phoneCountry} ${phone}`.trim();
      await register(
        email,
        password,
        name,
        fullPhone,
        referralCode || "",
        nif || "",
        companyName || "",
        accountType,
        undefined
      );

      // If the user opted in to buy physical Tag(s) — record the order in the
      // kit_campaign collection (the same collection admin uses to fulfil).
      // Subscribe-page submissions bypass the campaign capacity gate.
      if (wantKit) {
        try {
          const backend = process.env.REACT_APP_BACKEND_URL;
          await fetch(`${backend}/api/kit-campaign/leads`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name,
              email,
              phone: fullPhone,
              address: kitAddress.trim(),
              postal_code: kitPostalCode.trim(),
              city: kitCity.trim(),
              consent: true,
              source: "subscribe_page",
              plan_id: plan.id,
              qty: kitQty,
              template: kitTemplate,
              unit_price_eur: kitCost.unit,
              shipping_eur: kitCost.shipping,
              total_eur: kitCost.total,
            }),
          });
        } catch (_) {
          // Non-blocking — user account already created.
        }
      }

      // Free plan → straight to dashboard
      if (plan.monthly === 0) {
        navigate("/dashboard");
        return;
      }

      // Paid plan → create Stripe Checkout session and redirect.
      // We use /checkout-after-register because email verification is still
      // pending and the standard /checkout requires an authenticated session.
      const packageId = `${plan.id}_${billing}`;
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/subscriptions/checkout-after-register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.detail || "Erro ao criar sessão de pagamento");
      }
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Erro ao criar conta";
      toast.error(typeof msg === "string" ? msg : "Erro ao criar conta");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-10 px-4" data-testid="subscribe-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
          <Link to="/" className="flex items-center" aria-label="IMOCODE — voltar à página inicial">
            <Logo className="h-12 w-auto" />
          </Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
            Já tem conta?
          </Link>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <span
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition ${
                  step >= s
                    ? "bg-[#1a56db] text-white"
                    : "bg-white border-2 border-gray-200 text-gray-400"
                }`}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </span>
              {s < 3 && (
                <div
                  className={`w-12 h-0.5 ${
                    step > s ? "bg-[#1a56db]" : "bg-gray-200"
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>

        {/* STEP 1: Plan + Billing */}
        {step === 1 && (
          <div data-testid="subscribe-step-plan">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Escolhe o teu plano
              </h1>
              <p className="text-gray-500">
                Podes mudar ou cancelar a qualquer momento.
              </p>
            </div>

            {/* Billing toggle */}
            <div className="flex justify-center mb-7">
              <div className="inline-flex bg-white border border-gray-200 rounded-full p-1 shadow-sm">
                {BILLING_OPTIONS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setBilling(b.id)}
                    data-testid={`subscribe-billing-${b.id}`}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition relative ${
                      billing === b.id
                        ? "bg-[#1a56db] text-white shadow"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {b.label}
                    {b.save && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide opacity-90">
                        −{b.save}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {PLANS.map((p) => {
                const selected = planId === p.id;
                const priceTotal =
                  p.monthly === null
                    ? null
                    : +(p.monthly * billingOpt.multiplier * billingOpt.factor).toFixed(2);
                const priceMonthly =
                  p.monthly === null || p.monthly === 0
                    ? null
                    : +(p.monthly * billingOpt.factor).toFixed(2);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlanId(p.id)}
                    data-testid={`subscribe-plan-${p.id}`}
                    className={`text-left bg-white rounded-2xl p-5 border-2 transition relative ${
                      selected
                        ? "border-[#1a56db] ring-4 ring-blue-100 shadow-lg"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {p.popular && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-400 text-gray-900 text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                        POPULAR
                      </span>
                    )}
                    <p className="font-bold text-gray-900">{p.name}</p>
                    <p className="text-2xl font-extrabold text-gray-900 mt-1">
                      {p.monthly === null ? "Sob consulta" : fmt(priceMonthly ?? 0)}
                      {p.monthly !== null && p.monthly > 0 && (
                        <span className="text-xs text-gray-400 font-medium">/mês</span>
                      )}
                    </p>
                    {priceTotal !== null && billingOpt.id !== "monthly" && (
                      <p className="text-[11px] text-gray-500">
                        {fmt(priceTotal)} cobrado{" "}
                        {billingOpt.id === "annual" ? "anualmente" : "a cada 6 meses"}
                      </p>
                    )}
                    <ul className="mt-3 space-y-1.5">
                      {p.bullets.slice(0, 3).map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-1.5 text-xs text-gray-600"
                        >
                          <Check className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded-lg flex items-center gap-1">
                      <Gift className="w-3 h-3" /> {p.kits}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => goToStep(2)}
                disabled={plan.monthly === null}
                data-testid="subscribe-next-1"
                className="px-7 py-3 bg-[#1a56db] hover:bg-[#1648c0] disabled:opacity-50 text-white rounded-full font-semibold flex items-center gap-2 transition"
              >
                Continuar com {plan.name} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Register */}
        {step === 2 && (
          <form
            onSubmit={handleSubmitRegister}
            className="grid lg:grid-cols-3 gap-6"
            data-testid="subscribe-step-register"
          >
            <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Os teus dados</h2>

              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                Tipo de conta
              </p>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { id: "agent", label: "Agente", sub: "Individual", Icon: UserIcon },
                  { id: "agency", label: "Agência", sub: "Empresa", Icon: Building2 },
                  { id: "individual", label: "Particular", sub: "Próprio", Icon: Briefcase },
                ].map((opt) => {
                  const sel = accountType === opt.id;
                  const Ico = opt.Icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setAccountType(opt.id)}
                      data-testid={`subscribe-acct-${opt.id}`}
                      className={`p-3 rounded-xl border-2 text-center transition ${
                        sel
                          ? "border-[#1a56db] bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Ico
                        className={`w-5 h-5 mx-auto mb-1 ${
                          sel ? "text-[#1a56db]" : "text-gray-400"
                        }`}
                      />
                      <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                      <p className="text-[10px] text-gray-500">{opt.sub}</p>
                    </button>
                  );
                })}
              </div>

              <input
                type="text"
                placeholder="Nome completo"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="subscribe-name"
                className="w-full px-4 py-3 mb-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
              />
              <input
                type="email"
                placeholder="Email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="subscribe-email"
                className="w-full px-4 py-3 mb-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
              />
              <div className="relative mb-3">
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder="Palavra-passe (mín. 8 caracteres)"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="subscribe-password"
                  className="w-full px-4 py-3 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="grid grid-cols-[140px_1fr] gap-2 mb-3">
                <select
                  value={phoneCountry}
                  onChange={(e) => setPhoneCountry(e.target.value)}
                  data-testid="subscribe-phone-country"
                  className="px-2 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db] text-sm"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="Número de telemóvel"
                  required
                  pattern="[0-9 ]{6,}"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d ]/g, ""))}
                  data-testid="subscribe-phone"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
                />
              </div>

              {plan.monthly > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mt-4 mb-2">
                    Dados de Faturação
                  </p>
                  <input
                    type="text"
                    placeholder="NIF (9 dígitos)"
                    pattern="[0-9]{9}"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    className="w-full px-4 py-3 mb-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
                  />
                  <input
                    type="text"
                    placeholder="Nome da empresa (se aplicável)"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-4 py-3 mb-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
                  />
                </>
              )}

              <input
                type="text"
                placeholder="Código de referência (10% desconto)"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1a56db]"
              />

              {/* ---------- Optional Tag purchase add-on ----------------
                  The subscription (service) is one billing line. The Tag
                  (physical product) is a separate, OPTIONAL purchase. Price
                  varies per plan (admin-configurable discount). */}
              {kitForPlan.enabled && (
                <div className={`mt-5 rounded-2xl border-2 transition ${wantKit ? "border-amber-300 bg-amber-50/50" : "border-gray-200 bg-gray-50/40"}`}>
                  <label className="flex items-start gap-3 p-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wantKit}
                      onChange={(e) => setWantKit(e.target.checked)}
                      data-testid="subscribe-want-kit"
                      className="mt-0.5 w-5 h-5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
                        <Gift className="w-4 h-4 text-amber-600" />
                        Adicionar Tag física à subscrição
                        <span className="text-amber-700 font-bold">(opcional)</span>
                        {kitForPlan.discount_pct > 0 && (
                          <span className="ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                            −{kitForPlan.discount_pct}% para subscritores
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        Tag física com QR único pronta a colar.{" "}
                        <span className="font-semibold">A subscrição do serviço é independente</span>
                        {" "}da aquisição da tag — só pagas a tag se quiseres recebê-la.
                      </p>
                      <p className="text-xs text-gray-700 mt-1.5 flex items-baseline gap-2">
                        <span className="font-bold text-amber-700 text-base">
                          {fmt(kitForPlan.unit_price_eur)}
                        </span>
                        <span className="text-gray-500">/ unidade</span>
                        {kitForPlan.discount_pct > 0 && (
                          <span className="text-[11px] text-gray-400 line-through">
                            {fmt(kitForPlan.base_price_eur)}
                          </span>
                        )}
                      </p>
                    </div>
                  </label>

                  {wantKit && (
                    <div className="px-4 pb-4 pt-1 space-y-4" data-testid="subscribe-kit-details">
                      {/* Quantity selector */}
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
                          Quantidade <span className="text-gray-400 normal-case font-normal">(máx {kitForPlan.max_qty} neste plano)</span>
                        </p>
                        <div className="inline-flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setKitQty((q) => Math.max(1, q - 1))}
                            disabled={kitQty <= 1}
                            data-testid="subscribe-kit-qty-minus"
                            className="px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-30"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min={1}
                            max={kitForPlan.max_qty}
                            value={kitQty}
                            onChange={(e) => {
                              const n = parseInt(e.target.value || "1", 10);
                              const clamped = Math.max(1, Math.min(kitForPlan.max_qty, isNaN(n) ? 1 : n));
                              setKitQty(clamped);
                            }}
                            data-testid="subscribe-kit-qty"
                            className="w-14 text-center bg-transparent border-x border-gray-200 py-2 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setKitQty((q) => Math.min(kitForPlan.max_qty, q + 1))}
                            disabled={kitQty >= kitForPlan.max_qty}
                            data-testid="subscribe-kit-qty-plus"
                            className="px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-30"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Template choice */}
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
                          Template da Tag
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {(kitForPlan.template_options || ["predefinido", "personalizado"]).map(
                            (opt) => {
                              const sel = kitTemplate === opt;
                              const showSurcharge =
                                opt === "personalizado" &&
                                (kitForPlan.personalization_surcharge_eur || 0) > 0;
                              return (
                                <button
                                  type="button"
                                  key={opt}
                                  onClick={() => setKitTemplate(opt)}
                                  data-testid={`subscribe-kit-template-${opt}`}
                                  className={`p-2.5 rounded-xl border-2 text-left transition ${
                                    sel
                                      ? "border-amber-400 bg-white"
                                      : "border-gray-200 bg-white/60 hover:border-gray-300"
                                  }`}
                                >
                                  <p className="text-xs font-semibold text-gray-900 capitalize">
                                    {opt === "predefinido"
                                      ? "Template predefinido"
                                      : "Personalizada"}
                                  </p>
                                  <p className="text-[10px] text-gray-500 mt-0.5">
                                    {opt === "predefinido"
                                      ? "Envio mais rápido"
                                      : "Logo + cores da agência"}
                                    {showSurcharge && (
                                      <span className="ml-1 text-amber-700 font-semibold">
                                        +{fmt(kitForPlan.personalization_surcharge_eur)}
                                      </span>
                                    )}
                                  </p>
                                </button>
                              );
                            }
                          )}
                        </div>
                      </div>

                      {/* Shipping address */}
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
                          Morada de entrega
                        </p>
                        <input
                          type="text"
                          placeholder="Rua e número"
                          required={wantKit}
                          value={kitAddress}
                          onChange={(e) => setKitAddress(e.target.value)}
                          data-testid="subscribe-kit-address-street"
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 mb-2"
                        />
                        <div className="grid grid-cols-[140px_1fr] gap-2">
                          <input
                            type="text"
                            placeholder="0000-000"
                            required={wantKit}
                            value={kitPostalCode}
                            onChange={(e) => setKitPostalCode(e.target.value)}
                            data-testid="subscribe-kit-postal"
                            className="px-3 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                          />
                          <input
                            type="text"
                            placeholder="Cidade"
                            required={wantKit}
                            value={kitCity}
                            onChange={(e) => setKitCity(e.target.value)}
                            data-testid="subscribe-kit-city"
                            className="px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  className="text-sm text-gray-500 hover:text-gray-800"
                >
                  ← Voltar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="subscribe-submit"
                  className="px-7 py-3 bg-[#1a56db] hover:bg-[#1648c0] disabled:opacity-60 text-white rounded-full font-semibold flex items-center gap-2 transition"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {plan.monthly === 0 ? "Criar conta gratuita" : "Continuar para checkout"}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Order Summary — detailed */}
            <div className="bg-white rounded-3xl border border-gray-200 p-6 h-fit lg:sticky lg:top-6">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                A subscrever
              </p>
              <p className="text-2xl font-bold text-gray-900">
                Plano {plan.name}
              </p>
              <p className="text-sm text-gray-500 capitalize mb-4">
                {billingOpt.label}
                {billingOpt.save && (
                  <span className="ml-2 text-emerald-600 font-semibold">
                    poupa {billingOpt.save}
                  </span>
                )}
              </p>

              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                  O que está incluído
                </p>
                <ul className="space-y-1.5">
                  {plan.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-1.5 text-xs text-gray-700"
                    >
                      <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Order breakdown — clearly separates the SERVICE subscription
                  from the OPTIONAL physical Tag purchase. */}
              <div className="space-y-3 text-sm">
                {/* SUBSCRIPTION line */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">
                    Subscrição do serviço
                  </p>
                  {plan.monthly === 0 ? (
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-700">Plano Gratuito</span>
                      <span className="font-bold text-emerald-600">€0,00</span>
                    </div>
                  ) : (
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-700">
                        {fmt(monthlyEquivalent)}/mês × {billingOpt.multiplier}
                      </span>
                      <span className="font-bold text-gray-900">{fmt(total)}</span>
                    </div>
                  )}
                </div>

                {/* TAG line (only when opted in) */}
                {wantKit && (
                  <div className="pt-2 border-t border-gray-100" data-testid="subscribe-summary-kit">
                    <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1 flex items-center gap-1">
                      <Gift className="w-3 h-3" /> Aquisição de Tag física (opcional)
                    </p>
                    <div className="flex justify-between text-gray-600 text-xs">
                      <span>
                        Tag <span className="capitalize">{kitTemplate}</span> × {kitQty}
                        {kitForPlan.discount_pct > 0 && (
                          <span className="ml-1 text-emerald-600 font-semibold">
                            (−{kitForPlan.discount_pct}%)
                          </span>
                        )}
                      </span>
                      <span className="text-gray-700">{fmt(kitCost.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600 text-xs mt-1">
                      <span>Portes de envio</span>
                      <span className="text-gray-700">
                        {kitCost.shipping === 0 ? "Grátis" : fmt(kitCost.shipping)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-900 font-semibold text-sm mt-1.5 pt-1.5 border-t border-amber-100">
                      <span>Subtotal Tag</span>
                      <span>{fmt(kitCost.total)}</span>
                    </div>
                  </div>
                )}

                {/* TOTAL */}
                <div className="pt-3 border-t border-gray-200 flex justify-between items-baseline">
                  <span className="font-bold text-gray-900">Total hoje</span>
                  <span className="font-bold text-2xl text-[#1a56db]" data-testid="subscribe-grand-total">
                    {fmt(((total || 0) + (wantKit ? kitCost.total : 0)))}
                  </span>
                </div>

                {plan.monthly === 0 && !wantKit && (
                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    A subscrição é gratuita. A Tag física é opcional — podes
                    pedi-la mais tarde no teu dashboard.
                  </p>
                )}
                {plan.monthly > 0 && (
                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    Pagamento único —{" "}
                    {billingOpt.id === "annual"
                      ? "12 meses"
                      : billingOpt.id === "semiannual"
                      ? "6 meses"
                      : "1 mês"}{" "}
                    de acesso completo à plataforma.
                  </p>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-3 flex items-start gap-1">
                <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Pagamento processado pela Stripe. Os teus dados não são
                partilhados.
              </p>
            </div>
          </form>
        )}

        {/* STEP 3: Free-plan only confirmation (paid plans now redirect to Stripe) */}
        {step === 3 && (
          <div className="bg-white rounded-3xl border border-gray-200 p-8 md:p-12 text-center max-w-2xl mx-auto" data-testid="subscribe-step-success">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Conta criada com sucesso!
            </h2>
            <p className="text-gray-600 mb-6">
              Estás no plano gratuito. Já podes começar a criar QR Codes.
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="subscribe-go-dashboard"
                className="flex-1 px-5 py-3 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                Ir para o dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribePage;
